import { useAtomValue } from "jotai";
import {
  Activity,
  ArrowRight,
  FileCheck,
  Lock,
  Shield,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { authApi } from "@/services/auth";
import { isAuthenticatedAtom } from "@/stores/auth.atom";

interface HeroSectionProps {
  className?: string;
}

export default function HeroSection({ className = "" }: HeroSectionProps) {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const handleGoogleSignup = async () => {
    try {
      setIsGoogleLoading(true);
      const response = await authApi.getGoogleUrl();
      window.location.href = response.url;
    } catch (error) {
      console.error("Google signup error:", error);
      toast({
        title: "Lỗi đăng nhập",
        description: "Không thể kết nối với Google. Vui lòng thử lại.",
        variant: "destructive",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <section className={`px-4 py-16 md:py-24 ${className}`}>
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          {/* Logo and Brand */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img
              src="/logo/icon-full-color.png"
              alt="Locuno"
              className="h-12 md:h-16"
            />
            <div className="text-midnight-blue">
              <h1 className="text-3xl md:text-4xl font-bold">Locuno</h1>
              <p className="text-sm md:text-base text-midnight-blue/70">
                An tâm trong từng khoảnh khắc
              </p>
            </div>
          </div>

          {/* Main Heading */}
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-midnight-blue leading-tight">
            Vòng Tay An Toàn
            <br />
            <span className="text-burnt-orange relative">
              Cho Gia Đình Bạn
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-burnt-orange/30 rounded-full" />
            </span>
          </h2>

          {/* Description */}
          <p className="text-xl md:text-2xl text-midnight-blue/80 mb-8 max-w-4xl mx-auto leading-relaxed">
            Mang lại sự an tâm cho mẹ, sự tự do khám phá cho con.
            <br />
            Locuno là người bạn đồng hành không thể thiếu của mọi gia đình hiện
            đại.
          </p>

          {/* Social Proof */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-12 text-midnight-blue/70">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium">
                5,000+ gia đình tin dùng
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">
                99.9% thời gian hoạt động
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium">Bảo mật chuẩn quốc tế</span>
            </div>
          </div>

          {/* CTA Button - Hidden when user is authenticated */}
          {!isAuthenticated && (
            <div className="flex flex-col items-center gap-6 mb-16">
              <Button
                onClick={handleGoogleSignup}
                disabled={isGoogleLoading}
                size="lg"
                className="bg-white hover:bg-gray-50 text-gray-900 font-semibold px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200"
              >
                {isGoogleLoading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                    <span>Đang chuyển hướng...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <img
                      src="/icons/google.svg"
                      alt="Google"
                      className="w-6 h-6"
                    />
                    <span>Tạo Vòng Tròn An Toàn</span>
                    <ArrowRight className="ml-1 w-5 h-5" />
                  </div>
                )}
              </Button>

              <p className="text-sm text-midnight-blue/70">
                ✓ Miễn phí &nbsp;&nbsp; ✓ Đăng ký 1 click với Google
                &nbsp;&nbsp; ✓ Bảo mật tuyệt đối
              </p>
            </div>
          )}

          {/* App Store Links */}
          <div className="flex flex-col items-center gap-4 mb-12">
            <p className="text-midnight-blue/80 font-medium">
              Hoặc tải ứng dụng di động:
            </p>
            <div className="flex gap-4">
              <a
                href="https://apps.apple.com/vn/app/locuno/id6753800628"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/appstore.png"
                  alt="Download on App Store"
                  className="h-14 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.locunoapp"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/playstore.png"
                  alt="Get it on Google Play"
                  className="h-14 w-auto"
                />
              </a>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-midnight-blue/60">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-green-700" />
              <span>Bảo mật SSL</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-blue-700" />
              <span>Dữ liệu được mã hóa</span>
            </div>
            <div className="flex items-center gap-2">
              <FileCheck className="w-4 h-4 text-purple-700" />
              <span>Tuân thủ GDPR</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
